import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { useDebounce } from "@/hooks/use-debounce";
import { api } from "@/lib/api";
import {
  processQueryParams,
  type QueryParams,
} from "@/lib/process-query-params";

export interface WebsitePage {
  url: string;
  page_type: string;
  total_engagements?: number;
  people_engaged?: number;
  sessions?: number;
  accounts_engaged?: number;
  opportunities_influenced?: number;
  engagements_per_account?: number;
  engagements_per_person?: number;
  accounts_activated?: number;
  accounts_without_opps?: number;
  deal_contacts_engaged?: number;
  engagements_per_deal_contact?: number;
  [key: string]: string | number | undefined; // Allow dynamic metric properties
}

// WebsitePageWithMetrics is just WebsitePage - it already supports dynamic properties
export type WebsitePageWithMetrics = WebsitePage;

export interface WebsitePageSearchResponse {
  results: WebsitePage[];
  count: number;
  limit: number;
  offset: number;
  total?: number; // Total count for pagination
  client_warning?: string; // For performance warnings
}

export function useWebsitePageSearch({
  search = "",
  limit = 50,
  offset = 0,
  startDate,
  endDate,
  channelId,
  metrics,
  debounceMs = 300,
}: {
  search?: string;
  limit?: number;
  offset?: number;
  startDate?: string;
  endDate?: string;
  channelId?: string;
  metrics?: string[];
  debounceMs?: number;
} = {}) {
  const debouncedSearch = useDebounce(search, debounceMs);

  return useQuery({
    queryKey: [
      "website-pages",
      "search",
      {
        search: debouncedSearch,
        limit,
        offset,
        startDate,
        endDate,
        channelId,
        metrics,
      },
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (debouncedSearch) params.append("search", debouncedSearch);
      params.append("limit", limit.toString());
      params.append("offset", offset.toString());
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      if (channelId && channelId !== "all")
        params.append("channel_id", channelId);
      if (metrics && metrics.length > 0)
        params.append("metrics", metrics.join(","));

      params.append("sort_by", "url");
      // Sort ascending to show shorter URLs first, improving UX:
      // 1. Shorter URLs (domain roots, simple paths) surface before long nested paths
      // 2. Users can progressively refine search for longer, more specific URLs
      // 3. Mitigates pagination limitations by making accessible URLs visible first
      params.append("sort_direction", "asc");

      return api.get<WebsitePageSearchResponse>(
        `datahub/webpages/?${params.toString()}`,
      );
    },
    enabled: true, // Always enabled, even with empty search
  });
}

/**
 * Hook for fetching website pages with infinite scrolling
 * Now supports full query parameters including filters and sorting
 */
export function useInfiniteWebsitePageSearch(
  params: QueryParams & {
    channelId?: string;
    debounceMs?: number;
  } = {},
) {
  const { search = "", debounceMs = 300, ...restParams } = params;
  const debouncedSearch = useDebounce(search, debounceMs);

  return useInfiniteQuery<WebsitePageSearchResponse, Error>({
    queryKey: [
      "website-pages",
      "infinite",
      {
        ...restParams,
        search: debouncedSearch,
      },
    ],
    queryFn: async ({ pageParam }) => {
      const offset = typeof pageParam === "number" ? pageParam : 0;

      // Build query params with all filters, sorting, etc.
      const queryParams: QueryParams = {
        ...restParams,
        search: debouncedSearch,
        offset,
      };

      // Handle channelId separately as it needs special treatment
      if (params.channelId && params.channelId !== "all") {
        queryParams.channel_id = params.channelId;
      }

      // Process all parameters using the shared utility
      const searchParams = processQueryParams(queryParams);

      const response = await api.get<WebsitePageSearchResponse>(
        `datahub/webpages/?${searchParams.toString()}`,
      );

      return response;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce(
        (sum, page) => sum + page.results.length,
        0,
      );

      // If we have a total count from the backend, use it to determine if there are more pages
      if (lastPage.total !== undefined) {
        if (loadedCount >= lastPage.total) {
          return undefined; // We've loaded all pages
        }
        return loadedCount; // Return the next offset
      }

      // Fallback: determine if there are more pages based on whether the last page was full
      const limit = params.limit || 25;
      if (lastPage.results.length < limit) {
        // Last page had fewer results than limit, so we've reached the end
        return undefined;
      }

      // Otherwise, return the next offset
      return loadedCount;
    },
    enabled: true,
  });
}

// Extend the shared QueryParams interface for webpage-specific parameters
export interface WebpagesQueryParams extends QueryParams {
  channelId?: string;
  metrics?: string[];
  metric_filters?: {
    account_filters?: unknown;
    opportunity_filters?: unknown;
    touchpoint_filters?: unknown;
  };
}

/**
 * Fetch all webpages matching the given filters for CSV export
 * This bypasses pagination to get the complete dataset
 */
export function useWebpagesExport() {
  const mutation = useMutation<
    WebsitePageWithMetrics[],
    Error,
    WebpagesQueryParams | undefined,
    unknown
  >({
    mutationFn: async (params = {}) => {
      const allWebpages: WebsitePage[] = [];
      let offset = 0;
      let hasMoreData = true;

      // Paginate through all webpages
      while (hasMoreData) {
        // Create query params for this page
        const queryParams: WebpagesQueryParams = {
          ...params,
          limit: 500, // Override the limit that is passed in
          offset,
        };

        // Handle channelId separately as it needs special treatment
        if (params.channelId && params.channelId !== "all") {
          queryParams.channel_id = params.channelId;
        }

        // Process all parameters using the shared utility
        const searchParams = processQueryParams(queryParams);

        // Fetch the current page of webpages
        const response = await api.get<WebsitePageSearchResponse>(
          `datahub/webpages/?${searchParams.toString()}`,
        );

        // Add the results to our array
        allWebpages.push(...response.results);

        // Check if we should continue fetching (same logic as useInfiniteWebsitePageSearch)
        if (response.total !== undefined) {
          hasMoreData = allWebpages.length < response.total;
        } else {
          // Fallback: determine if there are more pages based on whether the last page was full
          hasMoreData = response.results.length >= 500; // 500 is our batch size
        }

        // Increment the offset for the next page
        offset += response.results.length;
      }

      // Return webpages as-is - they already have the correct structure
      // WebsitePage interface already supports dynamic metric properties via [key: string]
      return allWebpages as WebsitePageWithMetrics[];
    },
  });

  // Return the mutation object and a convenience trigger function
  return {
    ...mutation,
    exportWebpagesAsync: (params?: WebpagesQueryParams) =>
      mutation.mutateAsync(params),
  };
}
